import { debounce } from "lodash";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonGroup, Col, Form, Row, ToggleButton } from "react-bootstrap";
import { Typeahead, type TypeaheadRef } from "react-bootstrap-typeahead";
import type { Option } from "react-bootstrap-typeahead/types/types";
import {
  type Case,
  type CaseMetadata,
  type Client,
  type CollectionItem,
  DocumentType,
  type ExternalCase,
  type ExternalPartners,
  PartyPosition,
  PropoundingParty,
  type ViewCommunication,
} from "@/briefpoint-client";
import Button from "@/components/Button";
import CaseTypeSelect, {
  type CaseTypeOption,
} from "@/components/CaseManagement/CaseTypeSelect";
import ExternalPartnerConnection from "@/components/ExternalPartner/ExternalPartnerConnection";
import FederalSelect from "@/components/FederalSelect";
import Loading from "@/components/Loading";
import { useAuth } from "@/hooks/useAuth";
import SwapIcon from "@/images/swap_vert.svg?react";
import { FederalId } from "@/lib/constants";
import type {
  CaseResponse,
  ClientOption,
  ClientResponse,
  ExternalCaseResponse,
  FirmClient,
  JurisdictionResponse,
} from "@/lib/types";
import { externalCaseFilterCallback } from "@/lib/utils";
import JurisdictionSelect from "../JurisdictionSelect";
import styles from "./ReviewPage.module.scss";
import { blankId, CaliforniaId } from "./SourceDocumentReview";
import {
  CustomMenu,
  getJxValue,
  getVenueValue,
  NoSuggestCustomMenuClient,
  shouldShowOrDisable,
  updateJurisdictionAndVenue,
} from "./utils";

type GenericArrayProps = Array<CollectionItem> | null;

// TODO: refactor into a type and move to /lib/types.tsx
// See ReviewPageOne.tsx for an identical implementation.
export class CombinedCaseSearch {
  constructor(
    public title: string,
    public search: string,
    public partner?: ExternalPartners,
    public externalId?: string,
    public internalId?: string,
    public customOption?: boolean,
    public label?: string,
  ) {}
}

const buildSearchString = (_case: CaseResponse) => {
  return `${_case.shortTitle} ${_case.title}`;
};

const buildSearchStringExternal = (_case: ExternalCase) => {
  return `${_case.title}`;
};

export const mergeCases = (
  cases?: CaseResponse[],
  externalCases?: ExternalCaseResponse[],
) => {
  let mappedCases =
    cases?.map(
      (c) =>
        new CombinedCaseSearch(
          c.shortTitle ?? c.title ?? "",
          buildSearchString(c),
          c.integration?.partner,
          c.integration?.identifier ?? undefined,
          c.id,
        ),
    ) ?? [];
  if (!externalCases?.length) {
    return mappedCases;
  }

  const mappedExternal = externalCases
    .filter((x) => x.title)
    .map(
      (c) =>
        new CombinedCaseSearch(
          c.title ?? "",
          buildSearchStringExternal(c),
          c.partner,
          c.id ?? undefined,
          undefined,
        ),
    );

  for (let i = 0; i < mappedExternal.length; i++) {
    const element = mappedExternal[i];

    const internal = mappedCases.find(
      (x) =>
        x.partner === element.partner && x.externalId === element.externalId,
    );
    if (internal) {
      element.internalId = internal.internalId;
      mappedCases = mappedCases.filter(
        (c) => c.internalId !== internal.internalId,
      );
    }
  }

  return mappedCases.concat(mappedExternal);
};

const checkClientPosition = (
  _case: CaseResponse | undefined,
  propoundingParty: PropoundingParty | undefined,
): boolean =>
  _case?.clientPosition
    ? _case.clientPosition === PartyPosition.Defendant
    : propoundingParty === PropoundingParty.Defendant;

// Venue, Case#, Judge
function ReviewPageOnePd({
  documentType,
  docJurisdiction,
  jurisdiction,
  jurisdictions,
  officeJurisdictions,
  docVenue,
  venue,
  setVenue,
  setJurisdiction,
  caseNumber,
  setCaseNumber,
  judge,
  setJudge,
  showEdit,
  setShowEdit,
  _case,
  setCase,
  client,
  clients,
  propoundingParty,
  docPropoundingName,
  propoundingPartyName,
  respondingParty,
  respondingPartyName,
  pleadClientPos,
  setPleadClientPos,
  setRespondingParty,
  setPropoundingParty,
  setRespondingPartyName,
  setPropoundingPartyName,
  setClient,
  cases,
  caseMetaData,
  externalCases,
  filterExternalCases,
  setClientIsDefendant,
  isExternalCasesLoading,
  caseType,
  setCaseType,
  firm,
}: {
  documentType: DocumentType;
  communication: ViewCommunication[] | undefined;
  docJurisdiction: string | null | undefined;
  jurisdiction: JurisdictionResponse | undefined;
  jurisdictions?: JurisdictionResponse[] | undefined;
  officeJurisdictions?: string[];
  docVenue: string;
  venue: string;
  docPropoundingName: string | null | undefined;
  propoundingPartyName: string;
  respondingPartyName: string;
  caseNumber: string;
  judge: string;
  caseId?: string;
  showEdit: boolean;
  clientIsDefendant: boolean;
  isExternalCasesLoading?: boolean;
  pleadClientPos:
    | {
        name: string | null | undefined;
        clientPos: PropoundingParty | undefined;
      }
    | undefined;
  _case?: CaseResponse;
  caseMetaData: CaseMetadata | undefined;
  client?: ClientResponse;
  clients?: ClientOption[];
  cases?: CaseResponse[];
  externalCases?: ExternalCaseResponse[];
  propoundingParty: PropoundingParty | undefined;
  respondingParty: PropoundingParty | undefined;
  // `Case` type is intentional, for creates without clientId, id
  setCase: React.Dispatch<React.SetStateAction<Case | undefined>>;
  setShowEdit: React.Dispatch<React.SetStateAction<boolean>>;
  setJudge: React.Dispatch<React.SetStateAction<string>>;
  setPleadClientPos: React.Dispatch<
    React.SetStateAction<
      | {
          name: string | null | undefined;
          clientPos: PropoundingParty | undefined;
        }
      | undefined
    >
  >;
  setRespondingParty: React.Dispatch<
    React.SetStateAction<PropoundingParty | undefined>
  >;
  setPropoundingParty: React.Dispatch<
    React.SetStateAction<PropoundingParty | undefined>
  >;
  setRespondingPartyName: React.Dispatch<React.SetStateAction<string>>;
  setPropoundingPartyName: React.Dispatch<React.SetStateAction<string>>;
  setCaseNumber: React.Dispatch<React.SetStateAction<string>>;
  setJurisdiction: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >;
  setVenue: React.Dispatch<React.SetStateAction<string>>;
  // `Client` type is intentional, for creates without clientId, id
  setClient: React.Dispatch<React.SetStateAction<Client | undefined>>;
  setClientIsDefendant: React.Dispatch<React.SetStateAction<boolean>>;
  filterExternalCases: (filter?: string) => Promise<void>;
  caseType: string | null | undefined;
  setCaseType: React.Dispatch<React.SetStateAction<string | null | undefined>>;
  firm: FirmClient | undefined;
}) {
  const { user } = useAuth();
  // *** RESPONDING/PROPOUNDING RELATED LOGIC *** //
  const [isCaseDirty, setIsCaseDirty] = useState<boolean>(false);
  const [isClientPosDefendant, setIsClientPosDefendant] = useState<
    boolean | null
  >(checkClientPosition(_case, propoundingParty));
  const [defendantOptions] = useState<GenericArrayProps>(
    caseMetaData?.defendants ?? null,
  );
  // This ensures that in the case where the client is a defendant, the fields are swapped appropriately when this component is loaded in
  const [isPartiesSwapped, setIsPartiesSwapped] = useState<boolean>(
    checkClientPosition(_case, propoundingParty),
  );
  const [showClientMenu, setShowClientMenu] = useState<boolean>(false);
  const [showRespondingMenu, setShowRespondingMenu] = useState<boolean>(false);
  // We are now checking if jx/case numbers/venue values are valid, this dirty check is used to prevent the UI from showing the 'read only' UI
  // since the case changed might contain valid values to switch to the 'read only' mode
  const [isCaseChangeDirty, setIsCaseChangeDirty] = useState<boolean>(false);
  const syncCheckedCompleted = useRef<boolean>(false);

  const isCurClientMatch: boolean =
    _case?.clientId !== undefined &&
    client?.id !== undefined &&
    _case?.clientId === client?.id;

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore swapParties
  useEffect(() => {
    if (_case && _case.clientPosition === PartyPosition.Defendant) {
      swapParties(false, false);
    }
    // the below is to prevent the linter from having swapParties in the dependency array. swapParties is only required to run once
    // eslint-disable-next-line
  }, [_case]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore pleadClientPos
  useEffect(() => {
    if (client?.name && !syncCheckedCompleted.current) {
      const doesPropoundingMatchClient = client?.name === propoundingPartyName;
      if (!doesPropoundingMatchClient) setPropoundingPartyName(client?.name);
      setPleadClientPos((prevState) => {
        return {
          clientPos: prevState?.clientPos,
          name: client.name,
        };
      });
      syncCheckedCompleted.current = true;
    }
    // This is only required to run once to ensure client names and the propoundingPartyName match correctly.
    // eslint-disable-next-line
  }, [client]);

  // Swaps the current client's party name
  const swapPartyNames = (name: string | null | undefined) => {
    if (name === respondingPartyName) {
      return propoundingPartyName ?? "";
    }
    if (name === propoundingPartyName) {
      return respondingPartyName ?? "";
    }
    return name;
  };

  // Update the client state info when we update the client position and ensure if selected client already exists or not
  const updateClientPos = (
    clientPosition: PropoundingParty,
    shouldUpdateName?: boolean,
  ) => {
    setPleadClientPos((prevState) => {
      let newState: {
        name: string | null | undefined;
        clientPos: PropoundingParty | undefined;
      };
      if (shouldUpdateName) {
        const updatedName = swapPartyNames(prevState?.name);
        newState = { name: updatedName, clientPos: clientPosition };
      } else {
        newState = { name: prevState?.name, clientPos: clientPosition };
      }
      return newState;
    });
  };

  const updateClientOnSwap = (setToDefendant?: boolean) => {
    if (setToDefendant) {
      setClient((prevState) => ({
        name:
          prevState?.name === propoundingPartyName
            ? respondingPartyName
            : propoundingPartyName,
      }));
    } else {
      setClient((prevState) => ({
        name:
          prevState?.name === respondingPartyName
            ? propoundingPartyName
            : respondingPartyName,
      }));
    }
  };

  const handleClientPositionCheck = (checked: boolean) => {
    if (checked) {
      setClientIsDefendant(true);
      updateClientPos(PropoundingParty.Defendant);
      setIsClientPosDefendant(checked);
    } else {
      setClientIsDefendant(false);
      updateClientPos(PropoundingParty.Plaintiff);
      setIsClientPosDefendant(checked);
    }
    swapParties(true);
  };

  const swapParties = (isRadio?: boolean, shouldUpdateName = true) => {
    // Update the party swapped boolean to update the UI fields
    function handleSwap() {
      setIsPartiesSwapped((prevState) => !prevState);
      if (isPartiesSwapped) {
        updateClientOnSwap();
      } else {
        updateClientOnSwap(true);
      }

      setRespondingPartyName(propoundingPartyName);
      setPropoundingPartyName(respondingPartyName);
      setPropoundingParty(respondingParty ?? PropoundingParty.Defendant);
      setRespondingParty(propoundingParty ?? PropoundingParty.Plaintiff);
    }

    if (isRadio) {
      handleSwap();
    } else {
      if (isClientPosDefendant) {
        setClientIsDefendant(false);
        updateClientPos(PropoundingParty.Plaintiff, shouldUpdateName);
      } else {
        setClientIsDefendant(true);
        updateClientPos(PropoundingParty.Defendant, shouldUpdateName);
      }
      setIsClientPosDefendant((prevState) => !prevState);
      handleSwap();
    }
  };

  function handleOnPlaintiffClientInputChange(text: string) {
    if (text) {
      setClient({ name: text });
      setPropoundingPartyName(text);
      setShowClientMenu(true);
    } else {
      setClient(undefined);
      setPropoundingPartyName("");
      setShowClientMenu(false);
    }
  }

  function handleOnRespondingInputChange(text: string) {
    if (text) {
      setRespondingPartyName(text);
      setShowRespondingMenu(true);
    } else {
      setRespondingPartyName("");
      setShowRespondingMenu(false);
    }
  }

  function handlePlaintiffClientChange(e: Option[]): void {
    const val = e[0];
    if (val) {
      const v = val as { customOption: boolean; name: string };
      if (v.customOption) {
        setClient({ name: v.name });
        setPropoundingPartyName(v.name);
        setPleadClientPos((prevState) => ({
          clientPos: prevState?.clientPos,
          name: v.name,
        }));
        if (!isCaseDirty && !_case?.id) {
          setCase({
            shortTitle: isPartiesSwapped
              ? `${respondingPartyName} vs ${v.name}`
              : `${v.name} vs ${respondingPartyName}`,
          });
        }
        if (pleadClientPos?.clientPos === PropoundingParty.Plaintiff) {
          setPleadClientPos((prevState) => ({
            clientPos: prevState?.clientPos,
            name: v.name,
          }));
        }
        setShowClientMenu(false);
      } else {
        const c = val as ClientResponse;
        setClient(c);
        setPropoundingPartyName(c.name ?? "");
        if (!isCaseDirty && !_case?.id) {
          setCase({
            shortTitle: isPartiesSwapped
              ? `${respondingPartyName} vs ${c.name}`
              : `${c.name} vs ${respondingPartyName}`,
          });
        }
        setPleadClientPos((prevState) => ({
          clientPos: prevState?.clientPos,
          name: c.name ?? "",
        }));
        setShowClientMenu(false);
      }
    } else {
      setClient(undefined);
    }
  }

  function handleRespondingPartyChange(e: Option[]): void {
    const val = e[0];

    if (val) {
      const v = (val as { text: string }).text;
      setRespondingPartyName(v);
      if (!isCaseDirty && !_case?.id) {
        setCase({
          shortTitle: isPartiesSwapped
            ? `${v} vs ${propoundingPartyName ?? ""} `
            : `${propoundingPartyName ?? ""} vs ${v}`,
        });
      }
      setShowRespondingMenu(false);
      if (val) {
        setShowRespondingMenu(false);
      }
    }
  }

  // **** CASE/VENUE RELATED LOGIC **//
  // Checks if the current case is an existing case, if not, change toggle to 'New Case'
  const isExistingCase = !!_case && !!_case.id;

  // if the current case matches, default to the 'find case' field
  const [caseAction, setCaseAction] = useState<string>(
    isExistingCase || !showEdit ? "2" : "1",
  );
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [isExternalLoading, setIsLoadingExternal] = useState<boolean>(false);
  const caseTypeaheadRef = useRef<TypeaheadRef>(null);
  const clientTypeaheadRef = useRef<TypeaheadRef>(null);
  const respondTypeaheadRef = useRef<TypeaheadRef>(null);
  const venues = jurisdiction?.venues;
  const caseActionTypes = [
    { name: "New Case", value: "1" },
    { name: "Existing Case", value: "2" },
  ];

  // Case Type logic
  const resolvedJurisdiction = useMemo(() => {
    if (!jurisdictions || !firm) {
      return jurisdiction;
    }

    const isJsxBlank = _case?.jurisdiction === blankId;
    const firmJx =
      firm.offices?.find((x) => x.jurisdictions?.length)?.jurisdictions?.[0]
        ?.jurisdictionId ?? CaliforniaId;

    if (isJsxBlank) {
      return jurisdictions.find((j) => j.id === firmJx);
    }

    return (
      jurisdictions?.find((j) => j.id === _case?.jurisdiction) ??
      jurisdictions?.find((j) => j.id === firmJx) ??
      jurisdiction
    );
  }, [jurisdictions, firm, _case?.jurisdiction, jurisdiction]);

  const caseTypes = resolvedJurisdiction?.caseClasses
    ?.filter((c) => c.subTypes)
    .flatMap((c) => [c.subTypes])
    .flat();

  const caseTypeOptions: CaseTypeOption[] = useMemo(() => {
    if (!caseTypes || !resolvedJurisdiction?.caseClasses) {
      return [];
    }
    const vs = caseTypes?.flatMap((v) => [
      {
        value: v?.id ?? "",
        label: v?.shortName ?? "",
        key:
          resolvedJurisdiction?.caseClasses?.find(
            (x) => x.id === v?.caseClassId,
          )?.name ?? "",
      },
    ]);
    return vs;
  }, [caseTypes, resolvedJurisdiction?.caseClasses]);

  function handleCaseTypeChange(caseTypeValue: string) {
    const matchedType = caseTypeOptions.find((x) => x.value === caseTypeValue);
    if (matchedType) {
      setCaseType(matchedType.value);
    }
  }

  function handleCaseChange(e: Option[]): void {
    setIsCaseChangeDirty(true);
    const val = e[0];

    if (val) {
      const c = val as CombinedCaseSearch;
      let _case: Case | undefined = cases?.find(
        (x) =>
          x.id === c.internalId && x.integration?.identifier === c.externalId,
      );
      const existingClient = clients?.find((x) => x.id === _case?.clientId);

      if (!_case) {
        const integration = c.externalId
          ? { partner: c.partner, identifier: c.externalId }
          : undefined;
        _case = { shortTitle: c.title, integration };
      }

      // Check to see if the case has a valid Jx/Venue value and update it with anything extracted from the document
      updateJurisdictionAndVenue(
        docJurisdiction,
        docVenue,
        _case,
        handleUpdateJurisdiction,
        setVenue,
      );

      setCase(_case);
      setIsCaseDirty(true);
      if (existingClient) {
        setClient(existingClient);
        setPropoundingPartyName(existingClient.name ?? "");
        setPleadClientPos((prevState) => ({
          clientPos: prevState?.clientPos,
          name: existingClient.name ?? "",
        }));
      }
      setShowMenu(false);
      if (_case.venue) {
        setVenue((prevVenue) =>
          _case?.venue !== undefined && _case?.venue !== blankId
            ? _case.venue
            : prevVenue,
        );
      }

      if ((!!_case && !!_case.id) || _case.integration?.identifier) {
        setShowMenu(false);
        setCaseAction("2");
      } else {
        setCaseAction("1");
        setClient(undefined);
      }
    }
  }

  function handleToggleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.currentTarget.value;
    setCaseAction(value);
    setCase(undefined);
    caseTypeaheadRef.current?.focus();
    if (value === "1") {
      if (cases) {
        caseTypeaheadRef.current?.toggleMenu();
      }
      setClient(undefined);
    }
  }

  function handleOnInputChange(text: string) {
    if (text) {
      const isMatching = sortedCases.some((option) =>
        option.title?.toLowerCase().includes(text.toLowerCase()),
      );
      setCase({ shortTitle: text });
      setCaseNumber(caseNumber);
      setCaseAction("1");
      setShowMenu(user?.externalConnection?.isActive ? true : isMatching);
      setIsCaseDirty(true);
    } else {
      setCase(undefined);
      setShowMenu(false);
      setIsCaseDirty(false);
    }
    debouncedFilter(text);
  }

  function handleCaseChangeClick() {
    setClient(
      client && client.name === docPropoundingName
        ? client
        : { name: docPropoundingName ?? "" },
    );
    setPropoundingPartyName(docPropoundingName ?? "");
    setShowEdit(true);
  }

  const handleFilterChange = useCallback(
    async (filter?: string) => {
      setIsLoadingExternal(true);
      await filterExternalCases(filter);
      setIsLoadingExternal(false);
    },
    [filterExternalCases],
  );

  const debouncedFilter = useMemo(
    () =>
      debounce(async (filter?: string) => {
        await handleFilterChange(filter);
      }, 400),
    [handleFilterChange],
  );

  function renderClientPosFields() {
    return (
      <Form.Group id="new-pleading-select" className="indent-radio">
        <label htmlFor="pleadings-set-responding-plaintiff">
          What is your client's position?
        </label>
        <Row>
          <Col xs={12}>
            <Form.Check
              id="pleadings-set-responding-plaintiff"
              type="radio"
              inline
              label="Plaintiff"
              checked={!isClientPosDefendant}
              onChange={(_) => handleClientPositionCheck(!_.target.checked)}
            />
            <Form.Check
              id="pleadings-set-responding-defendant"
              type="radio"
              inline
              label="Defendant"
              checked={Boolean(isClientPosDefendant)}
              onChange={(_) => handleClientPositionCheck(_.target.checked)}
            />
          </Col>
        </Row>
      </Form.Group>
    );
  }

  function renderPropoundFields() {
    return (
      <Form.Group>
        <label className={styles.required} htmlFor="field-propounding">
          {isPartiesSwapped ? "Defendant" : "Plaintiff"}
        </label>
        <Typeahead
          allowNew={
            !_case?.clientId ||
            shouldShowOrDisable(_case, "showReadOnly", showEdit, true)
          }
          clearButton
          className={styles.typeahead}
          id="field-propounding"
          labelKey="name"
          onChange={(e) => handlePlaintiffClientChange(e)}
          onInputChange={handleOnPlaintiffClientInputChange}
          selected={(client && [client]) || []}
          size="sm"
          isInvalid={!client || !propoundingPartyName}
          open={showClientMenu}
          newSelectionPrefix="Create a new Client: "
          emptyLabel={
            !_case?.clientId
              ? "No matches found, Type to add a new client."
              : "No matches found."
          }
          renderMenu={(results, menuProps, state) => (
            <NoSuggestCustomMenuClient
              results={results as CombinedCaseSearch[]}
              menuProps={menuProps}
              state={state}
              showSourceLogos={!!user?.externalConnection?.isActive}
            />
          )}
          options={clients || []}
          ref={clientTypeaheadRef}
        />
      </Form.Group>
    );
  }

  function renderRespondingFields() {
    return (
      <div className="mb-3" style={{ position: "relative" }}>
        <Form.Group>
          <label className={styles.required} htmlFor="field-responding">
            {isPartiesSwapped ? "Plaintiff" : "Defendant"}
          </label>
          <Typeahead
            allowNew
            clearButton
            className={styles.typeahead}
            id="field-responding"
            labelKey="text"
            onChange={(e) => handleRespondingPartyChange(e)}
            onInputChange={handleOnRespondingInputChange}
            selected={(respondingPartyName && [respondingPartyName]) || []}
            size="sm"
            options={defendantOptions || []}
            open={showRespondingMenu}
            ref={respondTypeaheadRef}
          />
        </Form.Group>
      </div>
    );
  }

  function handleUpdateJurisdiction(jxId: string) {
    const defaultVenue =
      venues?.sort(
        (a, b) => a.shortName?.localeCompare(b.shortName ?? "") ?? 0,
      )[0].id ?? "";
    setVenue(defaultVenue);
    setJurisdiction(jxId);
  }

  function renderForm() {
    const canSelectJx = (officeJurisdictions?.length ?? 0) > 1;

    if (
      shouldShowOrDisable(_case, "showReadOnly", showEdit, true) &&
      !isCaseChangeDirty &&
      respondingPartyName
    ) {
      return (
        <>
          {!_case ? (
            <Loading isLoading={!_case} text="Loading case information..." />
          ) : (
            <>
              <div className="mt-3">
                <p className="label">Case</p>
                <p className="d-flex gap-2 align-items-center">
                  {_case?.shortTitle}
                  <Button
                    size="sm"
                    variant="link"
                    onClick={() => handleCaseChangeClick()}
                    className="p-0"
                  >
                    Change
                  </Button>
                </p>
              </div>
              {caseType && (
                <div className="mb-3">
                  <p className="label">Case Type</p>
                  <p>
                    {caseTypeOptions.find((x) => x.value === _case?.caseType)
                      ?.label ?? "-"}
                  </p>
                </div>
              )}
              {renderClientPosFields()}
              <h4 className="venue-header mt-2">
                Select the Propounding Party
              </h4>
              {renderPropoundFields()}
              {!isCurClientMatch && (
                <div className={styles.swapButtonWrapper}>
                  <div className={styles.swapButtonLine} />
                  <SwapIcon
                    className={styles.swapButton}
                    color="#4d5758"
                    title="Swap Parties"
                    onClick={() => swapParties()}
                  />
                  <div className={styles.swapButtonLine} />
                </div>
              )}
              <h4 className={`venue-header ${isCurClientMatch && "mt-3"}`}>
                Select the Responding Party
              </h4>
              {renderRespondingFields()}
              <div>
                <p className="label">Case Number</p>
                <p>{_case?.caseNumber ?? "-"}</p>
              </div>
              <div>
                <p className="label">Judge</p>
                <p>{_case?.judge ?? "-"}</p>
              </div>
            </>
          )}
        </>
      );
    }
    return (
      <>
        {user?.externalConnection?.isActive &&
        isExternalCasesLoading &&
        !externalCases ? (
          <Loading
            isLoading={isExternalCasesLoading && !externalCases}
            text="Loading case information..."
          />
        ) : (
          <>
            {!canSelectJx ? (
              <h3 className="venue-header">
                {jurisdiction?.id === FederalId ? "" : "State of "}
                {jurisdiction?.friendlyName}
              </h3>
            ) : (
              <>
                <label className={styles.required} htmlFor="jurisdiction">
                  Jurisdiction
                </label>
                <JurisdictionSelect
                  setJurisdiction={(value: string) => setJurisdiction(value)}
                  jurisdiction={getJxValue(jurisdiction)}
                  jurisdictions={jurisdictions}
                  yourJurisdictions={jurisdictions
                    ?.filter((j) => {
                      return officeJurisdictions?.includes(j.id ?? "");
                    })
                    .sort(
                      (a, b) =>
                        a.friendlyName?.localeCompare(b.friendlyName ?? "") ??
                        0,
                    )}
                  isDisabled={shouldShowOrDisable(_case, "disableJx", showEdit)}
                />
              </>
            )}
            <div className={showEdit ? "mb-4" : ""}>
              <label className={styles.required} htmlFor="venue-select">
                Venue
              </label>
              {!!jurisdiction?.id && jurisdiction.id === FederalId ? (
                <FederalSelect
                  setVenue={(e: string) => setVenue(e)}
                  venue={venue}
                  federalJurisdiction={jurisdiction}
                  jurisdictions={jurisdictions}
                  officeJxIds={officeJurisdictions}
                  isDisabled={shouldShowOrDisable(
                    _case,
                    "disableVenue",
                    showEdit,
                  )}
                />
              ) : (
                <Form.Select
                  required
                  id="venue-select"
                  disabled={shouldShowOrDisable(
                    _case,
                    "disableVenue",
                    showEdit,
                  )}
                  value={getVenueValue(_case, venue)}
                  onChange={(event) => setVenue(event.currentTarget.value)}
                >
                  <option value="" disabled>
                    Please Select
                  </option>
                  {venues
                    ?.sort(
                      (a, b) =>
                        a.shortName?.localeCompare(b.shortName ?? "") ?? 0,
                    )
                    .map((venue, index) => (
                      <option key={index} value={venue.id}>
                        {venue.shortName}
                      </option>
                    ))}
                </Form.Select>
              )}
            </div>
            <ExternalPartnerConnection />
            <div className={styles.caseSwitchToggle}>
              <ButtonGroup className={styles.bpToggleTxtSwitch}>
                {caseActionTypes.map((type, idx) => (
                  <ToggleButton
                    key={idx}
                    id={`radio-${idx}`}
                    className={
                      caseAction === type.value ? styles.btnChecked : ""
                    }
                    type="radio"
                    name="radio"
                    value={type.value}
                    checked={caseAction === type.value}
                    onChange={handleToggleChange}
                  >
                    {type.name}
                  </ToggleButton>
                ))}
              </ButtonGroup>
            </div>
            {!isCurClientMatch && renderClientPosFields()}
            <h4 className="venue-header mt-2">Select the Propounding Party</h4>
            {renderPropoundFields()}
            {!isCurClientMatch && (
              <div className={styles.swapButtonWrapper}>
                <div className={styles.swapButtonLine} />
                <SwapIcon
                  className={styles.swapButton}
                  color="#4d5758"
                  title="Swap Parties"
                  onClick={() => swapParties()}
                />
                <div className={styles.swapButtonLine} />
              </div>
            )}
            <h4 className={`venue-header ${isCurClientMatch && "mt-3"}`}>
              Select the Responding Party
            </h4>
            {renderRespondingFields()}
            <div className={styles.swapButtonWrapper}>
              <div className={styles.swapButtonLine} />
            </div>
            <label className={styles.required} htmlFor="case">
              Case
            </label>
            <div className="mb-3" style={{ minHeight: "40px" }}>
              <Typeahead
                className={`${styles.typeahead} ${
                  _case && user?.externalConnection?.isActive
                    ? _case.integration?.partner
                      ? styles[`external-partner-${_case.integration.partner}`]
                      : styles.externalPartnerNone
                    : ""
                }`}
                onChange={(e) => handleCaseChange(e)}
                onInputChange={handleOnInputChange}
                selected={
                  (_case &&
                    [_case].map(
                      (c) =>
                        new CombinedCaseSearch(
                          c.shortTitle ?? c.title ?? "",
                          buildSearchString(c),
                          c.integration?.partner,
                          c.integration?.identifier ?? undefined,
                          c.id,
                        ),
                    )) ||
                  []
                }
                clearButton
                filterBy={(option, props) =>
                  externalCaseFilterCallback(option, props, user, externalCases)
                }
                flip={false}
                isInvalid={!_case}
                id="case"
                emptyLabel={
                  caseAction === "1" &&
                  "No matches found, this will create a new case."
                }
                //
                onFocus={
                  caseAction === "1"
                    ? () => setShowMenu(false)
                    : () => setShowMenu(true)
                }
                options={sortedCases}
                open={showMenu}
                size="sm"
                placeholder={
                  caseAction === "1"
                    ? "Create a new case..."
                    : "Find an existing case..."
                }
                labelKey="title"
                renderMenu={(results, menuProps, state) => (
                  <CustomMenu
                    results={results as CombinedCaseSearch[]}
                    menuProps={menuProps}
                    state={state}
                    showSourceLogos={!!externalCases?.length}
                  />
                )}
                ref={caseTypeaheadRef}
                isLoading={isExternalLoading}
              />
            </div>
            <div className="mb-3">
              <Form.Group>
                <label className={styles.required} htmlFor="caseType">
                  Case Type
                </label>
                <div className={!_case?.caseType ? styles.caseTypeValid : ""}>
                  <CaseTypeSelect
                    isDisabled={!!isExistingCase && !!_case?.caseType}
                    options={caseTypeOptions}
                    onChange={handleCaseTypeChange}
                    selected={_case?.caseType ?? caseType ?? ""}
                  />
                </div>
              </Form.Group>
            </div>
            {/* Make case # field required for any doc type except pleadings */}
            <div className="mb-3">
              <Form.Group>
                <label htmlFor="case-number">Case Number</label>
                <Form.Control
                  required={documentType !== DocumentType.Pleading}
                  disabled={
                    shouldShowOrDisable(_case, "disableCaseNum", showEdit) &&
                    isExistingCase
                  }
                  id="case-number"
                  size="sm"
                  value={_case?.caseNumber ?? caseNumber}
                  onChange={(event) => setCaseNumber(event.currentTarget.value)}
                />
              </Form.Group>
            </div>
            <div>
              <label htmlFor="judge">Judge</label>
              <Form.Control
                value={_case?.judge ?? judge}
                disabled={
                  shouldShowOrDisable(_case, "disableJudge", showEdit) &&
                  isExistingCase
                }
                id="judge"
                size="sm"
                onChange={(event) => setJudge(event.currentTarget.value)}
              />
            </div>
          </>
        )}
      </>
    );
  }

  const sortedCases: CombinedCaseSearch[] = mergeCases(
    cases,
    externalCases,
  ).sort((a, b) => {
    // Prioritize cases with a defined "partner" to the top
    if (a.partner && !b.partner) {
      return -1;
    }
    if (!a.partner && b.partner) {
      return 1;
    }

    // Prioritize new cases (cases without an ID) to the top
    const caseIdA = a.internalId || a.externalId || "";
    const caseIdB = b.internalId || b.externalId || "";
    if (!caseIdA && caseIdB) {
      return -1;
    }
    if (caseIdA && !caseIdB) {
      return 1;
    }

    // Continue with the regular alphabetical sort by title
    const shortTitleA = a.title || "";
    const shortTitleB = b.title || "";
    return shortTitleA.localeCompare(shortTitleB);
  });

  return <div id="case-client-confirm">{renderForm()}</div>;
}

export default ReviewPageOnePd;
